import { Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { BannerType } from '@design-system/components/banner/m-banner';
import { ExportTableType, Filter } from '@portal/app/shared/types';
import {
  FeatureConfigService,
  FeatureName
} from '@portal/app/shared/services/feature-config.service';
import {
  DataAvailabilityName,
  DatasetAvailabilityService
} from '@portal/app/shared/services/dataset-availability.service';
import { DatasetExportService } from '@portal/app/shared/services/dataset-export.service';
import { ContextStore } from '@portal/app/shared/state/context.store';
import { ViewService } from '@portal/app/shared/services/view.service';
import { SelectionService } from '@portal/app/shared/services/selection.service';
import { MmmBannerService } from '@portal/app/shared/components/mmm-banner/mmm-banner.service';

@Component({
  selector: 'portal-mmm-export-banner',
  templateUrl: './mmm-export-banner.component.html',
  styleUrls: ['./mmm-export-banner.component.scss']
})
export class MmmExportBannerComponent implements OnInit, OnDestroy {
  public showBanner = false;
  public showDownloadResults = false;
  public bannerConfig = {
    type: BannerType.primary,
    title: 'Your MMM-powered results are now ready!',
    titleNoDownloadResults: 'Your account is now fully powered by MMM!'
  };

  exportOption = '';

  public exportOptions = [
    {
      label: 'CSV',
      value: 'csv'
    },
    {
      label: 'EXCEL',
      value: 'xlsx'
    }
  ];

  private filters: Filter[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private readonly featureConfigService: FeatureConfigService,
    private readonly datasetAvailabilityService: DatasetAvailabilityService,
    private readonly datasetExportService: DatasetExportService,
    private readonly contextStore: ContextStore,
    public readonly viewService: ViewService,
    private readonly selectionService: SelectionService,
    private readonly mmmBannerService: MmmBannerService
  ) {}

  ngOnInit() {
    const alreadyShown = !!localStorage.getItem(this.getLocalStorageKey());
    if (!alreadyShown) {
      this.contextStore.filterContext
        .pipe(takeUntil(this.destroy$))
        .subscribe((filters: Filter[]) => {
          this.filters = filters;
          this.datasetAvailabilityService.fetchData(this.filters);
        });
      this.subscribeToChanges();
    }
  }

  private subscribeToChanges(): void {
    combineLatest([
      this.featureConfigService.data$,
      this.datasetAvailabilityService.data$,
      this.mmmBannerService.data$
    ])
      .pipe(
        map(([feature, availability, mmmBanner]) => ({
          feature,
          availability,
          mmmBanner
        })),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: ({ mmmBanner }) => {
          this.showBanner = this.featureConfigService.isFeatureEnabled(
            FeatureName.mimExportBanner
          );

          if (mmmBanner.mimModelVersion === 1) {
            const hasData = this.datasetAvailabilityService.hasData(
              DataAvailabilityName.mimExportHasData
            );
            if (hasData) {
              this.showDownloadResults = true;
            } else {
              this.showBanner = false;
            }
          } else if (mmmBanner.mimModelVersion === 2) {
            this.showDownloadResults = false;
          }
        },
        error: (err: unknown) => console.error(err) // handle errors appropriately
      });
  }

  onExportChange(exportType: string) {
    if (exportType) {
      this.exportData(exportType as ExportTableType);
    }
  }

  exportData(format: ExportTableType): void {
    this.datasetExportService.exportData(this.filters, format);
  }

  handleClose() {
    localStorage.setItem(this.getLocalStorageKey(), 'true');
    this.showBanner = false;
  }

  getLocalStorageKey(): string {
    const { clientId, brandId } = this.selectionService.getClientIdAndBrandId();
    return `mimBannerAlreadyShown_${clientId}_${brandId}`;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
